import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  api, ApiFailure, ageLabel, balanceDueOf, genderShort,
  VISIT_TYPE_LABEL, VISIT_STATUS_LABEL, PAYMENT_MODE_LABEL,
  type Patient, type Doctor, type Visit as VisitT, type Vitals, type Payment,
  type PaymentMode, type ClinicSettings,
} from '../api'
import { CategoryPill } from '../components/PatientCategory'
import { vitalFlags, vitalClass, type VitalFlag } from '../clinical'

interface VisitDetail {
  visit: VisitT
  patient: Patient
  doctor: Doctor
  vitals: Vitals[]
  payment: Payment | null
}

/**
 * One reading in the table. Warns, never blocks - a reading of 210/130 is
 * exactly the one to record - and says why in words as well as colour, so the
 * warning survives a monochrome print and a screen reader.
 */
function VitalCell({ value, flag }: { value: React.ReactNode; flag: VitalFlag }) {
  if (value == null || value === '') return <td className="num">—</td>
  if (flag.level === 'normal') return <td className="num">{value}</td>
  return (
    <td className="num">
      <span className={vitalClass(flag)}>
        {value}
        <span className="vital-tag"> {flag.label}</span>
      </span>
    </td>
  )
}

const BLANK_VITALS = {
  weightKg: '', heightCm: '', bpSystolic: '', bpDiastolic: '', pulseBpm: '',
  temperatureF: '', spo2Pct: '', respiratoryRate: '', bloodSugar: '',
  bloodSugarType: '' as '' | 'fasting' | 'post_prandial' | 'random',
}

export default function Visit() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [data, setData] = useState<VisitDetail | null>(null)
  const [clinic, setClinic] = useState<ClinicSettings | null>(null)
  const [vitalsForm, setVitalsForm] = useState({ ...BLANK_VITALS })
  const [pay, setPay] = useState({
    fee: '', discount: '0', discountReason: '', amountPaid: '',
    mode: 'cash' as PaymentMode, referenceNo: '',
  })
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    if (!id) return
    const res = await api.get<VisitDetail>(`/api/visits/${id}`)
    setData(res)
    if (!res.payment) {
      const fee = res.visit.visitType === 'follow_up' ? res.doctor.followUpFee : res.doctor.consultationFee
      setPay((p) => ({ ...p, fee, amountPaid: fee }))
    }
  }, [id])

  useEffect(() => {
    void load()
    void api.get<{ settings: ClinicSettings | null }>('/api/settings').then((r) => setClinic(r.settings))
  }, [load])

  async function saveVitals(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setMsg(null)
    try {
      const payload = Object.fromEntries(
        Object.entries(vitalsForm).filter(([, v]) => v !== ''),
      )
      if (Object.keys(payload).length === 0) {
        setMsg({ kind: 'error', text: 'Enter at least one reading.' })
        setBusy(false)
        return
      }
      await api.post(`/api/visits/${id}/vitals`, payload)
      setVitalsForm({ ...BLANK_VITALS })
      await load()
      setMsg({ kind: 'ok', text: 'Reading recorded.' })
    } catch (err) {
      setMsg({ kind: 'error', text: err instanceof ApiFailure ? err.message : 'Could not save.' })
    } finally {
      setBusy(false)
    }
  }

  async function takePayment(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setMsg(null)
    try {
      await api.post(`/api/visits/${id}/payment`, {
        fee: pay.fee,
        discount: pay.discount || '0',
        discountReason: pay.discountReason || null,
        amountPaid: pay.amountPaid,
        mode: pay.mode,
        referenceNo: pay.referenceNo || null,
      })
      await load()
      setMsg({ kind: 'ok', text: 'Payment recorded.' })
    } catch (err) {
      setMsg({ kind: 'error', text: err instanceof ApiFailure ? err.message : 'Could not record payment.' })
    } finally {
      setBusy(false)
    }
  }

  if (!data) return <div className="loading">Loading…</div>

  const { visit, patient, doctor, vitals, payment } = data
  const net = (Number(pay.fee || 0) - Number(pay.discount || 0)).toFixed(2)
  const balanceDue = balanceDueOf(payment)

  return (
    <>
      <div className="page-head no-print">
        <h2>Visit — {patient.fullName}</h2>
        <p>
          Token #{visit.tokenNo} · Visit #{visit.visitNo} · {VISIT_TYPE_LABEL[visit.visitType]} ·{' '}
          {doctor.fullName} · {visit.visitDate}
        </p>
      </div>

      {msg && <div className={`msg ${msg.kind} no-print`}>{msg.text}</div>}

      <div className="panel no-print">
        <div className="patient-strip">
          <div>
            <strong>{patient.fullName}</strong> <span className="mono">{patient.patientCode}</span>
          </div>
          <div className="mono">
            {ageLabel(patient)} / {genderShort(patient.gender)}{patient.mobile ? ` · ${patient.mobile}` : ''}
            {patient.bloodGroup ? ` · ${patient.bloodGroup}` : ''}
          </div>
          <div><CategoryPill category={patient.category} /></div>
          {patient.allergies && <div className="allergy">Allergies: {patient.allergies}</div>}
          {patient.chronicConditions && <div className="chronic">{patient.chronicConditions}</div>}
          {visit.chiefComplaint && <div className="complaint">Complaint: {visit.chiefComplaint}</div>}
        </div>
        <div className="actions">
          <span className={`pill ${visit.status === 'completed' ? 'on' : 'off'}`}>
            {VISIT_STATUS_LABEL[visit.status]}
          </span>
          <button className="secondary" onClick={() => navigate('/reception')}>Back to reception</button>
        </div>
      </div>

      <div className="panel no-print">
        <h3>Vitals</h3>
        {vitals.length > 0 && (
          <div className="table-wrap" style={{ marginBottom: 16 }}>
            <table>
              <thead>
                <tr><th>Time</th><th>Wt</th><th>Ht</th><th>BMI</th><th>BP</th><th>Pulse</th><th>Temp</th><th>SpO₂</th><th>Sugar</th></tr>
              </thead>
              <tbody>
                {vitals.map((v) => {
                  const f = vitalFlags(v)
                  return (
                    <tr key={v.id}>
                      <td className="num subtle">{new Date(v.recordedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</td>
                      <td className="num">{v.weightKg ?? '—'}</td>
                      <td className="num">{v.heightCm ?? '—'}</td>
                      <td className="num">{v.bmi ?? '—'}</td>
                      <VitalCell
                        value={v.bpSystolic != null ? `${v.bpSystolic}/${v.bpDiastolic}` : null}
                        flag={f.bp}
                      />
                      <VitalCell value={v.pulseBpm} flag={f.pulse} />
                      <VitalCell value={v.temperatureF} flag={f.temp} />
                      <VitalCell value={v.spo2Pct} flag={f.spo2} />
                      <VitalCell
                        value={v.bloodSugar
                          ? `${v.bloodSugar} ${v.bloodSugarType === 'fasting' ? 'F' : v.bloodSugarType === 'post_prandial' ? 'PP' : 'R'}`
                          : null}
                        flag={f.sugar}
                      />
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        <form onSubmit={(e) => void saveVitals(e)}>
          <p className="panel-note">
            {vitals.length > 0
              ? 'Adding a reading keeps the earlier ones — a BP re-check is a new row, not an edit.'
              : 'Leave anything not measured blank.'}
          </p>
          <div className="grid three">
            <div className="field">
              <label htmlFor="w">Weight (kg)</label>
              <input id="w" type="number" step="0.1" value={vitalsForm.weightKg}
                onChange={(e) => setVitalsForm({ ...vitalsForm, weightKg: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="h">Height (cm)</label>
              <input id="h" type="number" step="0.1" value={vitalsForm.heightCm}
                onChange={(e) => setVitalsForm({ ...vitalsForm, heightCm: e.target.value })} />
            </div>
            <div className="field">
              <label>BP (mmHg)</label>
              <div className="bp-pair">
                <input aria-label="Systolic" type="number" placeholder="120" value={vitalsForm.bpSystolic}
                  onChange={(e) => setVitalsForm({ ...vitalsForm, bpSystolic: e.target.value })} />
                <span>/</span>
                <input aria-label="Diastolic" type="number" placeholder="80" value={vitalsForm.bpDiastolic}
                  onChange={(e) => setVitalsForm({ ...vitalsForm, bpDiastolic: e.target.value })} />
              </div>
            </div>
            <div className="field">
              <label htmlFor="pl">Pulse (bpm)</label>
              <input id="pl" type="number" value={vitalsForm.pulseBpm}
                onChange={(e) => setVitalsForm({ ...vitalsForm, pulseBpm: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="tp">Temperature (°F)</label>
              <input id="tp" type="number" step="0.1" value={vitalsForm.temperatureF}
                onChange={(e) => setVitalsForm({ ...vitalsForm, temperatureF: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="sp">SpO₂ (%)</label>
              <input id="sp" type="number" value={vitalsForm.spo2Pct}
                onChange={(e) => setVitalsForm({ ...vitalsForm, spo2Pct: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="bs">Blood sugar (mg/dL)</label>
              <input id="bs" type="number" step="0.1" value={vitalsForm.bloodSugar}
                onChange={(e) => setVitalsForm({ ...vitalsForm, bloodSugar: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="bst">Sugar type</label>
              <select id="bst" value={vitalsForm.bloodSugarType}
                onChange={(e) => setVitalsForm({ ...vitalsForm, bloodSugarType: e.target.value as typeof vitalsForm.bloodSugarType })}>
                <option value="">—</option>
                <option value="fasting">Fasting</option>
                <option value="post_prandial">Post-prandial</option>
                <option value="random">Random</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="rr">Resp. rate (/min)</label>
              <input id="rr" type="number" value={vitalsForm.respiratoryRate}
                onChange={(e) => setVitalsForm({ ...vitalsForm, respiratoryRate: e.target.value })} />
            </div>
          </div>
          <div className="actions">
            <button type="submit" disabled={busy}>
              {vitals.length > 0 ? 'Add another reading' : 'Record vitals'}
            </button>
          </div>
        </form>
      </div>

      <div className="panel no-print">
        <h3>Payment</h3>
        {payment ? (
          <>
            <div className="grid three">
              <div className="field"><label>Receipt</label><div className="mono">{payment.receiptNo}</div></div>
              <div className="field"><label>Fee</label><div className="num">₹{payment.fee}</div></div>
              <div className="field"><label>Discount</label><div className="num">₹{payment.discount}</div></div>
              <div className="field"><label>Net payable</label><div className="num"><strong>₹{payment.netPayable}</strong></div></div>
              <div className="field"><label>Paid</label><div className="num">₹{payment.amountPaid} <span className="pill on">{PAYMENT_MODE_LABEL[payment.mode]}</span></div></div>
              <div className="field"><label>Status</label><div><span className={`pill ${payment.status === 'paid' ? 'on' : 'warn'}`}>{payment.status}</span></div></div>
              {balanceDue > 0 && (
                <div className="field">
                  <label>Balance due</label>
                  <div className="num vital-critical">₹{balanceDue.toFixed(2)}</div>
                </div>
              )}
            </div>
            {payment.discountReason && (
              <p className="panel-note" style={{ marginTop: 12 }}>Discount reason: {payment.discountReason}</p>
            )}
            <div className="actions">
              <button onClick={() => window.print()}>Print receipt</button>
            </div>
            <p className="panel-note" style={{ marginTop: 10 }}>
              Payments cannot be edited. A refund or correction is recorded as a separate adjustment.
            </p>
          </>
        ) : (
          <form onSubmit={(e) => void takePayment(e)}>
            <div className="grid three">
              <div className="field">
                <label htmlFor="fee">Consultation fee</label>
                <input id="fee" type="number" step="1" value={pay.fee}
                  onChange={(e) => setPay({ ...pay, fee: e.target.value, amountPaid: (Number(e.target.value) - Number(pay.discount || 0)).toString() })} />
                <span className="hint">{VISIT_TYPE_LABEL[visit.visitType]} rate for {doctor.fullName}</span>
              </div>
              <div className="field">
                <label htmlFor="disc">Discount</label>
                <input id="disc" type="number" step="1" value={pay.discount}
                  onChange={(e) => setPay({ ...pay, discount: e.target.value, amountPaid: (Number(pay.fee || 0) - Number(e.target.value || 0)).toString() })} />
              </div>
              <div className="field">
                <label>Net payable</label>
                <div className="net">₹{net}</div>
              </div>
            </div>
            {Number(pay.discount) > 0 && (
              <div className="field" style={{ marginTop: 14 }}>
                <label htmlFor="dr">Reason for discount</label>
                <input id="dr" value={pay.discountReason}
                  onChange={(e) => setPay({ ...pay, discountReason: e.target.value })} required />
              </div>
            )}
            <div className="grid three" style={{ marginTop: 14 }}>
              <div className="field">
                <label htmlFor="paid">Amount paid</label>
                <input id="paid" type="number" step="1" value={pay.amountPaid}
                  onChange={(e) => setPay({ ...pay, amountPaid: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="mode">Mode</label>
                <select id="mode" value={pay.mode}
                  onChange={(e) => setPay({ ...pay, mode: e.target.value as PaymentMode })}>
                  {Object.entries(PAYMENT_MODE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="field">
                <label htmlFor="ref">Reference no.</label>
                <input id="ref" value={pay.referenceNo} placeholder="UPI txn / card last 4"
                  onChange={(e) => setPay({ ...pay, referenceNo: e.target.value })} />
              </div>
            </div>
            <div className="actions">
              <button type="submit" disabled={busy}>Record payment</button>
            </div>
          </form>
        )}
      </div>

      {payment && clinic && (
        <div className="print-area">
          <div className="receipt">
            <div className="receipt-head">
              <div className="receipt-clinic">{clinic.clinicName}</div>
              <div className="receipt-sub">
                {[clinic.addressLine1, clinic.addressLine2, clinic.city, clinic.pincode].filter(Boolean).join(', ')}
              </div>
              {clinic.phone && <div className="receipt-sub">Phone: {clinic.phone}</div>}
            </div>
            <div className="receipt-title">Consultation Receipt</div>
            <table className="receipt-meta">
              <tbody>
                <tr><td>Receipt no.</td><td className="r">{payment.receiptNo}</td></tr>
                <tr><td>Date</td><td className="r">{new Date(payment.collectedAt).toLocaleString('en-IN')}</td></tr>
                <tr><td>Patient</td><td className="r">{patient.fullName} ({patient.patientCode})</td></tr>
                <tr><td>Age / Sex</td><td className="r">{ageLabel(patient)} / {genderShort(patient.gender)}</td></tr>
                <tr><td>Doctor</td><td className="r">{doctor.fullName}{doctor.qualifications ? `, ${doctor.qualifications}` : ''}</td></tr>
                <tr><td>Visit</td><td className="r">#{visit.visitNo} · {VISIT_TYPE_LABEL[visit.visitType]} · Token #{visit.tokenNo}</td></tr>
              </tbody>
            </table>
            <table className="receipt-lines">
              <tbody>
                <tr><td>Consultation fee</td><td className="r">₹{payment.fee}</td></tr>
                {Number(payment.discount) > 0 && (
                  <tr><td>Discount{payment.discountReason ? ` (${payment.discountReason})` : ''}</td><td className="r">− ₹{payment.discount}</td></tr>
                )}
                <tr className="total"><td>Net payable</td><td className="r">₹{payment.netPayable}</td></tr>
                <tr><td>Paid by {PAYMENT_MODE_LABEL[payment.mode]}{payment.referenceNo ? ` (${payment.referenceNo})` : ''}</td><td className="r">₹{payment.amountPaid}</td></tr>
                {/* A part-paid receipt that stops at "paid" reads as settled.
                    The balance is the line the patient and the desk both need. */}
                {balanceDue > 0 && (
                  <tr className="due">
                    <td><strong>Balance due</strong></td>
                    <td className="r"><strong>₹{balanceDue.toFixed(2)}</strong></td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className="receipt-foot">
              {balanceDue > 0 && (
                <div className="receipt-due-note">
                  This is a part payment. ₹{balanceDue.toFixed(2)} remains outstanding.
                </div>
              )}
              Computer-generated receipt. No signature required.
            </div>
          </div>
        </div>
      )}
    </>
  )
}
